import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { Platform } from '@root/entities/platform'
import { MenuDotsIcon } from '@root/shared/ui-kit'

import {
  notificationCenterScreenAtom,
  resetNotificationCenterSettingsAction,
} from '../atoms'

import { NotificationCenterSettings } from './notification-center-settings'
import { NotificationCenterBody } from './notification-center-view'

import type { ClassNameProps } from '@root/shared/react'

type Props = {
  open?: boolean
  value?: Platform.Platform
  onChange: (platform: Platform.Platform) => void
} & ClassNameProps

export const NotificationCenter: React.FC<Props> = ({
  className,
  open: openProp = false,
  value = Platform.Terminal,
  onChange,
}) => {
  const haptics = useHapticFeedback()
  const [open, setOpen] = useState<boolean>(openProp)
  const [screen, setScreen] = useAtom(notificationCenterScreenAtom)
  const resetSettings = useAction(resetNotificationCenterSettingsAction)
  const overlayRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | undefined>(undefined)
  const screenRef = useRef(screen)
  screenRef.current = screen

  const close = useCallback(() => {
    haptics.impactOccurred('light')
    setOpen(false)
    resetSettings()
    setScreen('view')
  }, [haptics, resetSettings, setScreen])

  const handleOpen = () => {
    haptics.impactOccurred('light')
    setOpen(true)
  }

  useEffect(() => {
    if (!open) {
      return
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    overlayRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
      previousFocusRef.current = undefined
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      event.stopPropagation()
      if (screenRef.current === 'edit') {
        haptics.impactOccurred('light')
        resetSettings()
        setScreen('view')
        return
      }
      close()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, close, haptics, resetSettings, setScreen])

  return (
    <>
      <Trigger
        type="button"
        data-open={open || undefined}
        onClick={handleOpen}
        className={className}
      >
        <MenuDotsIcon size={18} />
      </Trigger>
      {open
        ? createPortal(
            <Overlay ref={overlayRef} tabIndex={-1}>
              <AnimatePresence mode="wait" initial={false}>
                {screen === 'view' ? (
                  <Screen
                    key="view"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    <NotificationCenterBody
                      value={value}
                      onChange={onChange}
                      onClose={close}
                    />
                  </Screen>
                ) : (
                  <Screen
                    key="settings"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    <NotificationCenterSettings />
                  </Screen>
                )}
              </AnimatePresence>
            </Overlay>,
            document.body,
          )
        : undefined}
    </>
  )
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  outline: none;
  overflow: hidden;
  animation: ${fadeIn} 100ms ease-out forwards;
`

const Screen = styled(motion.div)`
  height: 100%;
`

const Trigger = styled.button`
  color: var(--text-12);
  background: var(--background-3);

  display: grid;
  align-content: center;
  justify-content: center;
  overflow: hidden;
  align-items: center;
  cursor: pointer;

  grid-template-columns: auto;
  height: 28px;
  width: 28px;
  border: 0;
  padding: 0;

  text-align: left;
  position: relative;
  border-radius: 48px;
  backdrop-filter: blur(6px);
  transition: all 0.2s ease-in-out;

  &[data-open] {
    color: var(--text-9);
    background: var(--backgroundA-1);
  }

  margin-inline: -5px 0;
  margin-top: 1px;
`
